'use client';

import { useIntl } from '@capsicum/common';
import {
	Button,
	ButtonInSection,
	ButtonSection,
	CheckboxInput,
	SubmitButton,
	Text,
	useCopyToClipboard
} from '@capsicum/common-web';
import { CopyClipboardIcon } from '@capsicum/common-web/icons';
import { useExportAndCloseAccountCompletion } from '@capsicum/features/account/shared';
import { privateKeyHexAtom } from '@capsicum/socios-core';
import { useAtom } from 'jotai';
import { type ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

export const ExportAndCloseAccountCompletion = () => {
	const { $t } = useIntl();
	const { intlTexts, resolver, supportLink } = useExportAndCloseAccountCompletion();
	const [privateKey, setPrivateKey] = useAtom(privateKeyHexAtom);

	const accountClosedSaveYourPrivateKey = $t('settings.accountManagement.accountClosedSaveYourPrivateKey', {
		support: (content: ReactNode) => <S.PolicyLink href={supportLink}>{content}</S.PolicyLink>
	});

	const formMethods = useForm({
		mode: 'onChange',
		resolver
	});
	const { copyToClipboard: copyPrivateKey } = useCopyToClipboard({
		label: intlTexts.copiedToClipboard,
		value: privateKey || ''
	});

	const handleSubmit = () => setPrivateKey(null);

	return (
		<S.Wrapper>
			<S.Container>
				<S.Content>
					<S.PrivateKeyContainer>
						<S.Title variant="textTopBar">{intlTexts.yourPrivateKey}</S.Title>
						<S.Key variant="text8">{privateKey}</S.Key>
						<Button
							iconEnd={{ icon: { src: CopyClipboardIcon } }}
							testID="settings-accountManagement-exportAndCloseAccount-copyButton"
							text={intlTexts.copyPrivateKeyButton}
							onClick={copyPrivateKey}
						/>
					</S.PrivateKeyContainer>
				</S.Content>
			</S.Container>

			<S.Container>
				<S.Content>
					<S.PrivateKeyContainer>
						<Text variant="text3">{accountClosedSaveYourPrivateKey}</Text>
					</S.PrivateKeyContainer>
				</S.Content>
			</S.Container>
			<FormProvider {...formMethods}>
				<S.Form onSubmit={formMethods.handleSubmit(handleSubmit)}>
					<S.Container>
						<S.Content>
							<CheckboxInput
								name="checkbox"
								testID="settings-accountManagement-exportAndCloseAccount-completion-checkbox"
							>
								{intlTexts.confirmHavingSavedPrivateKey}
							</CheckboxInput>
						</S.Content>
					</S.Container>
					<ButtonSection>
						<ButtonInSection
							as={SubmitButton}
							color="brand"
							disabled={!formMethods.formState.isValid}
							testID="settings-accountManagement-logout-button"
							text={intlTexts.logOut}
							type="button"
							variant="outlined"
						/>
					</ButtonSection>
				</S.Form>
			</FormProvider>
		</S.Wrapper>
	);
};

namespace S {
	export const Form = styled.form`
		display: contents;
	`;
	export const Wrapper = styled.div`
		display: flex;
		flex: 1;
		flex-direction: column;
		padding: ${({ theme }) => `${theme.spacings.medium} 0`};
		gap: ${({ theme }) => theme.spacings.medium};
		padding: ${({ theme }) => `${theme.spacings.medium} ${theme.spacings.small}`};
	`;
	export const Key = styled(Text)`
		color: ${({ theme }) => theme.colors.text.brand.enabled};
		text-align: center;
		word-break: break-all;
	`;
	export const PolicyLink = styled.a`
		${({ theme }) => theme.fonts.text.text3}
		color: ${({ theme }) => theme.colors.text.brand.enabled};
	`;
	export const Container = styled.div`
		display: flex;
		flex-direction: column;
		padding: ${({ theme }) => theme.spacings.small};
		gap: ${({ theme }) => theme.spacings.small};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.border.radius.medium}
	`;
	export const Content = styled.div`
		flex-direction: column;
		display: flex;
		gap: ${({ theme }) => theme.spacings.large};
	`;
	export const PrivateKeyContainer = styled(Content)`
		align-items: center;
		text-align: center;
		margin-block: ${({ theme }) => theme.spacings.small};
	`;

	export const Title = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;
}
